import { createContext } from "react"
import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { CATEGORIES_REQUEST_PARAMS } from "../constants"
import cubeClient from "../cubeClient"
import {
  useCategoriesState,
  CategoryData,
  CategoriesState,
  UseCategoriesState
} from "../states"

type Props = {
  enabled: boolean
  nodeIds?: string[]
  children?: (
    query: UseQueryResult<CategoryData[]>,
    categories: CategoriesState
  ) => React.ReactNode
}

export const CategoriesDataContext = createContext<
  UseCategoriesState & { loading: boolean }
>(null)

const CategoriesDataProvider: React.FC<Props> = ({
  enabled = false,
  nodeIds,
  children
}) => {
  const categoriesState = useCategoriesState()
  const { state, onAddCategories } = categoriesState

  const query = useQuery<CategoryData[], CategoryData[]>({
    queryKey: ["reportingCategories", enabled, nodeIds],
    queryFn: () => {
      if (!enabled) {
        return null
      }

      return cubeClient
        .get<CategoryData[], CategoryData[]>("/", {
          params: {
            query: JSON.stringify({
              dimensions: CATEGORIES_REQUEST_PARAMS.dimensions,
              ...(nodeIds && {
                filters: [
                  {
                    ...CATEGORIES_REQUEST_PARAMS.filters[0],
                    values: [...nodeIds]
                  }
                ]
              })
            })
          }
        })
        .then(response => {
          onAddCategories(response ?? [])
          return response ?? []
        })
    }
  })

  return (
    <CategoriesDataContext.Provider
      value={{ ...categoriesState, loading: !!query?.isFetching }}
    >
      {children?.(query, state)}
    </CategoriesDataContext.Provider>
  )
}

export default CategoriesDataProvider
